import { useContext, useEffect, useState } from "react";

import { Alert, Col, Flex, Row, Typography } from "antd";
import { AppContext } from "App";
import Confetti from "assets/animation/confetti-blown-up.json";
import purpleInfoIcon from "assets/images/new-questionnaire/purple-info.svg";
import taxAccountantImage from "assets/images/new-questionnaire/submit/tax-accountant-review.svg";
import BankDetailsForm from "components/basic/BankDetailsForm/BankDetailsForm";
import QuestionnaireButtonFooterV2 from "components/basic/Footer/QuestionnaireButtonFooterV2";
import LottieAnimation from "components/basic/Spinner/Lottie";
import Lottie from "lottie-react";
import { isMobile as isMobileDevice, useDeviceData } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import { useCompleteDocumentMutation } from "services/auth";
import { IUser } from "types/auth";
import { logGoogleAnalyticsEvent } from "utils/analytics";
import { scrollToTop } from "utils/common";
import { logError } from "utils/sentry";

import VeriffStartPage from "./IdVerification/IdVerificationStartPage";
import { TaxSummary } from "./TaxSummary";
import { useAppSelector } from "../../../../../redux/hooks";
import { RootState } from "../../../../../redux/store";
import "styles/questionnaire/submitPage.less";
import { veriffApi } from "../../../../../services/veriff";

interface ISubmitPageProps {
    user: IUser;
    handlePrev: () => void;
}

/* eslint-disable */
enum SubmitStep {
    ID_VERIFICATION = "id-verification",
    BANK_DETAILS = "bank-details",
    TAX_ACCOUNTANT = "tax-accountant",
    TAX_SUMMARY = "tax-summary",
}
/* eslint-enable */

export const SubmitPage = ({ user, handlePrev }: ISubmitPageProps) => {
    const { isMobile } = useContext(AppContext);
    const navigate = useNavigate();
    const deviceData = useDeviceData(window.navigator.userAgent);
    const { currentDocument } = useAppSelector(
        (state: RootState) => ({
            currentDocument: state.document.currentDocument,
            currentDocumentYear: state.document.currentDocumentYear,
        })
    );
    const {
        data: isIdVerificationCompletedData,
        refetch: refetchIdVerification,
    } = veriffApi.endpoints.verificationStatus.useQuery({});
    const idVerificationComplete = isIdVerificationCompletedData?.completed;
    const [completeDocument] = useCompleteDocumentMutation();
    const [pageLoading, setPageLoading] = useState(true);
    const [showConfetti, setShowConfetti] = useState(false);
    const [step, setStep] = useState<SubmitStep | null>(null);
    const [, setLearnMoreModalOpen] = useState(false);

    useEffect(() => {
        refetchIdVerification();
    }, [refetchIdVerification]);

    useEffect(() => {
        if (idVerificationComplete === undefined || !user || !currentDocument) return;

        if (!idVerificationComplete) {
            setStep(SubmitStep.ID_VERIFICATION);
        } else if (!user.paymentDetails?.confirmed) {
            setStep(SubmitStep.BANK_DETAILS);
        } else if (currentDocument.payment?.pricingBreakdown?.pricingCategory === "accountantReviewRequested") {
            setStep(SubmitStep.TAX_ACCOUNTANT);
        } else {
            setStep(SubmitStep.TAX_SUMMARY);
        }
        setPageLoading(false);
    }, [idVerificationComplete, user, currentDocument]);

    const handleSubmit = async () => {
        try {
            const result = await completeDocument({ documentId: currentDocument?._id });

            if ("data" in result) {
                const { data } = result;
                if (data && !data.document?.answersOB?.shouldComplete) {
                    setShowConfetti(true);
                    setTimeout(() => {
                        setShowConfetti(false);
                        setPageLoading(true);
                        setTimeout(() => {
                            setPageLoading(false);
                            navigate("/profile");
                        }, 4000);
                    }, 2000);
                } else {
                    logError("Failed to complete the document", {
                        function: "handleSubmit",
                        message: "Document completion failed",
                        clientId: user?.id,
                        documentId: currentDocument?._id,
                        data: data,
                    });
                }
            }
        } catch (error) {
            logError("Error submitting document", {
                function: "handleSubmit",
                message: "Error submitting document",
                clientId: user?.id,
                documentId: currentDocument?._id,
                error: error,
            });
        }
        logGoogleAnalyticsEvent("confirm_and_submit_clicked", {
            user_id: user?.id,
            document_id: currentDocument?._id,
            device: deviceData?.device,
            os: deviceData?.os,
            browser: deviceData?.browser,
            platform: isMobileDevice ? "mobile" : "desktop",
        });
    };

    const handlePreviousStep = () => {
        switch (step) {
        case SubmitStep.ID_VERIFICATION:
            handlePrev();
            break;

        case SubmitStep.BANK_DETAILS:
            idVerificationComplete ? handlePrev() : setStep(SubmitStep.ID_VERIFICATION);
            break;

        case SubmitStep.TAX_ACCOUNTANT:
            setStep(SubmitStep.BANK_DETAILS);
            break;

        case SubmitStep.TAX_SUMMARY: {
            const isAccountantReview = currentDocument?.payment?.pricingBreakdown?.pricingCategory === "accountantReviewRequested";
            setStep(isAccountantReview ? SubmitStep.TAX_ACCOUNTANT : SubmitStep.BANK_DETAILS);
            break;
        }

        default:
            break;
        }
    };

    const bankDetailsComponent = (
        <div className="submit-page__bank-details">
            <div className="submit-page__bank-details-container">
                <Typography
                    className="bold fs24 w100 mt0"
                >
                    Add Bank Details
                </Typography>
                <Typography
                    className="w100 fs16 grey-6-color mt0"
                >
                    If you're due a refund, HMRC will pay it directly into this bank account. <span onClick={() => setLearnMoreModalOpen(true)} className="submit-page__bank-details__learn-more">Learn More</span>
                </Typography>
                <BankDetailsForm advanceStep={() => {
                    const nextStep = currentDocument?.payment?.pricingBreakdown?.pricingCategory === "accountantReviewRequested"
                        ? SubmitStep.TAX_ACCOUNTANT
                        : SubmitStep.TAX_SUMMARY;
                    setStep(nextStep);
                }} user={user} skipEnabled={true} />
            </div>
        </div>
    );

    const taxSummaryComponent = (
        <div className="submit-page__tax-summary">
            <div className="tax-summary__header">Tax Return Summary - {currentDocument?.year}</div>
            <div className="tax-summary__subheading">Please review and submit your tax return</div>
            <div className="submit-page__summary-tax-container">
                <Alert className="tax-summary__alert" message="Click ‘Confirm & Submit’ below to finalise your tax return. Your taxes will not be processed until you confirm and submit." type="info" showIcon icon={<img src={purpleInfoIcon} alt="info icon"/>}/>
                <TaxSummary handleSubmit={handleSubmit} user={user} isMobile={isMobileDevice}/>
            </div>
            {showConfetti &&
                    <div className="submit-page__submit-animation">
                        <Lottie
                            animationData={Confetti}
                            loop={true}
                            autoplay={true}
                        />
                    </div>
            }
        </div>
    );

    const taxAccountantComponent = (
        <div className="submit-page__tax-accountant">
            {window.innerWidth > 768 && <img src={taxAccountantImage} alt="tax accountant review" className="submit-page__tax-accountant-image" />}

            <Typography className="bold fs20 mt10">Let's Finalise Your Appointment With An Accountant</Typography>
            <Typography className={`${isMobile ? "taLeft" : "taCenter"} fs16 grey-6-color mt10`}>
                You’ll be matched with a qualified accountant for a one-on-one review of your tax return. They’ll walk you through everything, answer your questions, and submit your return on your behalf.
            </Typography>
            {isMobile && <a
                href="https://taxzapuk.setmore.com/" target="_blank" rel="noopener noreferrer"
                className="shared__primary-button submit-page__tax-accountant-button mt20 w100"
            >
                Book Appointment
            </a>}
            <Row className="w100 mt20" gutter={[16, 16]}>
                <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                    <div className="bold mb10">What's Included:</div>
                    <ul className="submit-page__tax-accountant-list submit-page__tax-accountant-list--tick">
                        <div>Personalised review by a certified accountant</div>
                        <div>One-on-one walkthrough of your return</div>
                        <div>Submission of your return on your behalf</div>
                        <div>Answers to any tax questions you have</div>
                    </ul>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                    <div className="bold mb10">What Happens Next:</div>
                    <ul className="submit-page__tax-accountant-list submit-page__tax-accountant-list--numbered">
                        <div>Click "Book Appointment"</div>
                        <div>If you've already paid for an accountant review, you're all set, no additional payment required</div>
                        <div>If not, you’ll be prompted to pay an additional fee before joining the call</div>
                    </ul>
                </Col>
            </Row>
            {!isMobile && <a
                href="https://taxzapuk.setmore.com/" target="_blank" rel="noopener noreferrer"
                className="shared__primary-button submit-page__tax-accountant-button mt20"
            >
                Book Appointment
            </a>}
        </div>

    );

    const stepComponentMap = {
        [SubmitStep.ID_VERIFICATION]: (
            <VeriffStartPage advanceStep={() => setStep(SubmitStep.BANK_DETAILS)} />
        ),
        [SubmitStep.BANK_DETAILS]: bankDetailsComponent,
        [SubmitStep.TAX_ACCOUNTANT]: taxAccountantComponent,
        [SubmitStep.TAX_SUMMARY]: taxSummaryComponent,
    };

    return (
        <div>
            <LottieAnimation isLoading={pageLoading} backgroundColor={"white"} />
            {step ? stepComponentMap[step] : null}
            <div className={"mt100"}>
                <QuestionnaireButtonFooterV2>
                    <Flex justify="space-between" align="center" className="w100 p20">
                        <button
                            className={"shared__secondary-button"}
                            onClick={() => {
                                scrollToTop();
                                handlePreviousStep();
                            }}
                        >
                            Back
                        </button>
                        <div></div>
                    </Flex>
                </QuestionnaireButtonFooterV2>
            </div>
        </div>
    );
};
